import { Layers, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

interface TechnologySectionProps {
  variant: "men" | "women";
}

const TechnologySection = ({ variant }: TechnologySectionProps) => {
  const isDark = variant === "women";
  
  return (
    <section className={`py-20 md:py-32 ${isDark ? "bg-navy" : "bg-cream"}`}>
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/20 mb-8">
            <Layers className="w-8 h-8 text-gold" />
          </div>
          <h2 className={`font-serif text-3xl md:text-4xl lg:text-5xl font-bold mb-4 ${isDark ? "text-cream" : "text-navy"}`}>
            לא עוד סווייפ.
          </h2>
          <p className={`text-xl ${isDark ? "text-cream/80" : "text-muted-foreground"}`}>
            הכירו את פרוטוקול EVA
          </p>
        </div>

        {/* The 50/30/20 Model Visual */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className={`rounded-2xl p-8 md:p-12 ${isDark ? "bg-navy-light border border-gold/20" : "bg-cream-dark border border-gold/30"}`}>
            <div className="grid md:grid-cols-3 gap-8">
              {/* Emotional Core - 50% */}
              <div className="text-center group">
                <div className="relative mb-6">
                  <div className="w-28 h-28 mx-auto rounded-full bg-gradient-to-br from-gold/30 to-gold/10 flex items-center justify-center border-2 border-gold/40 transition-all duration-300 group-hover:scale-105 group-hover:border-gold">
                    <span className="font-serif text-4xl font-bold text-gold">50%</span>
                  </div>
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-16 h-1 bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
                </div>
                <h3 className={`font-serif text-xl font-bold mb-2 ${isDark ? "text-cream" : "text-navy"}`}>
                  ליבה רגשית
                </h3>
                <p className={`text-sm leading-relaxed ${isDark ? "text-cream/60" : "text-muted-foreground"}`}>
                  צרכים עמוקים, שפת אהבה, דפוסי התקשרות
                </p>
              </div>

              {/* Values - 30% */}
              <div className="text-center group">
                <div className="relative mb-6">
                  <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-gold/25 to-gold/5 flex items-center justify-center border-2 border-gold/30 transition-all duration-300 group-hover:scale-105 group-hover:border-gold">
                    <span className="font-serif text-3xl font-bold text-gold">30%</span>
                  </div>
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
                </div>
                <h3 className={`font-serif text-xl font-bold mb-2 ${isDark ? "text-cream" : "text-navy"}`}>
                  ערכים ותפיסות
                </h3>
                <p className={`text-sm leading-relaxed ${isDark ? "text-cream/60" : "text-muted-foreground"}`}>
                  השקפת עולם, יעדים משותפים, סגנון חיים
                </p>
              </div>

              {/* Attraction - 20% */}
              <div className="text-center group">
                <div className="relative mb-6">
                  <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-gold/20 to-gold/5 flex items-center justify-center border-2 border-gold/20 transition-all duration-300 group-hover:scale-105 group-hover:border-gold">
                    <span className="font-serif text-2xl font-bold text-gold">20%</span>
                  </div>
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
                </div>
                <h3 className={`font-serif text-xl font-bold mb-2 ${isDark ? "text-cream" : "text-navy"}`}>
                  משיכה והעדפות
                </h3>
                <p className={`text-sm leading-relaxed ${isDark ? "text-cream/80" : "text-muted-foreground"}`}>
                  כימיה, העדפות אישיות, סגנון חיים
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Explanation Text */}
        <div className="max-w-2xl mx-auto text-center">
          <p className={`text-lg md:text-xl leading-relaxed mb-8 ${isDark ? "text-cream/80" : "text-muted-foreground"}`}>
            אפליקציות דייטים מתמקדות במראה ובמיקום. 
            <span className="text-gold font-semibold"> פרוטוקול EVA מסנן את הרעש </span>
            ומתמקד בהתאמה עמוקה. כל שידוך נבנה על פוטנציאל לטווח ארוך — לא על כימיה ראשונית.
          </p>
          <Link 
            to="/eva-protocol" 
            className="inline-flex items-center gap-2 text-gold hover:text-gold/80 transition-colors font-medium"
          >
            <span>למדו עוד על פרוטוקול EVA</span>
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TechnologySection;